import { useCallback } from 'react';
import type { AuthorizationServiceRoot } from 'src/apis/authorization-service/types';
import type { OrganisationDetailsResponse } from 'src/apis/organisation-service/types';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useIsEnd2EndTest } from 'src/hooks/use-is-e2e-test';
import { useSafeLocalStorage } from 'src/hooks/use-storage';
import { useStorybook } from 'src/hooks/use-storybook';

const BECOME_A_SHIPPER = 'BECOME_A_SHIPPER';
const OBSOLETE_PERMISSION_GROUPS = 'OBSOLETE_PERMISSION_GROUPS';

export type WelcomeModalType =
  | typeof BECOME_A_SHIPPER
  | typeof OBSOLETE_PERMISSION_GROUPS;

export const useWelcomeModal = () => {
  const safeLocalStorage = useSafeLocalStorage();
  const { isStorybook } = useStorybook();
  const isEnd2EndTest = useIsEnd2EndTest();
  const cognitoUser = useCognitoUser();

  const shouldShowWelcomeModal =
    !!safeLocalStorage.getItem('WELCOME_MODAL') &&
    !isStorybook &&
    !isEnd2EndTest &&
    !!cognitoUser;

  const setWelcomeModalForLogin = useCallback(
    (
      organisationDetails: OrganisationDetailsResponse | null,
      authorizationServiceRoot: AuthorizationServiceRoot | null
    ): void => {
      const welcomeModalType = getWelcomeModalType(
        organisationDetails,
        authorizationServiceRoot
      );

      if (welcomeModalType) {
        safeLocalStorage.setItem('WELCOME_MODAL', welcomeModalType);
      } else {
        safeLocalStorage.removeItem('WELCOME_MODAL');
      }
    },
    [safeLocalStorage]
  );

  const currentModalType = safeLocalStorage.getItem(
    'WELCOME_MODAL'
  ) as WelcomeModalType | null;

  const dismissWelcomeModal = useCallback(() => {
    safeLocalStorage.removeItem('WELCOME_MODAL');
  }, [safeLocalStorage]);

  return {
    shouldShowWelcomeModal,
    setWelcomeModalForLogin,
    currentModalType,
    dismissWelcomeModal,
  };
};

const canBecomeAShipper = (
  organisationDetails: OrganisationDetailsResponse | null
): boolean => {
  if (!organisationDetails) {
    return false;
  }

  const { _links } = organisationDetails;
  const organisationHasRole = Boolean(
    _links.shipperDetails ||
      _links.ssoDetails ||
      _links.lsoDetails ||
      _links.tsoDetails
  );

  return !organisationHasRole;
};

const hasObsoletePermissionGroups = (
  authorizationServiceRoot: AuthorizationServiceRoot | null
): boolean => {
  if (!authorizationServiceRoot) {
    return false;
  }

  return Boolean(authorizationServiceRoot.obsoletePermissionGroups?.length);
};

/**
 * BECOME_A_SHIPPER: should take a precedence over OBSOLETE_PERMISSION_GROUPS
 */
const getWelcomeModalType = (
  organisationDetails: OrganisationDetailsResponse | null,
  authorizationServiceRoot: AuthorizationServiceRoot | null
): WelcomeModalType | null => {
  if (canBecomeAShipper(organisationDetails)) {
    return BECOME_A_SHIPPER;
  }

  if (hasObsoletePermissionGroups(authorizationServiceRoot)) {
    return OBSOLETE_PERMISSION_GROUPS;
  }

  return null;
};
